import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { format } from 'date-fns';
import { WaitingListEntry, addContactNote, ContactNote } from '../../firebase/services/waitingListService';

interface ContactNotesDialogProps {
  open: boolean;
  onClose: () => void;
  entry: WaitingListEntry;
  onTransferToSlip: (entry: WaitingListEntry) => void;
}

const formatDate = (date: Date | { toDate: () => Date }) => {
  const dateObj = date instanceof Date ? date : date.toDate();
  return format(dateObj, 'MMM d, yyyy h:mm a');
};

const ContactNotesDialog: React.FC<ContactNotesDialogProps> = ({
  open,
  onClose,
  entry,
  onTransferToSlip
}) => {
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      setLoading(true);
      setError(null);
      await addContactNote(entry.id!, newNote);
      setNewNote('');
      // Refresh the entry data
      onClose();
    } catch (err) {
      setError('Failed to add note');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Contact Notes - {entry.firstName} {entry.lastName}
      </DialogTitle>
      <DialogContent>
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Vessel Details
          </Typography>
          <Typography variant="body2">
            {entry.vesselName} ({entry.vesselLength}' - {entry.vesselYear} {entry.vesselMake} {entry.vesselModel})
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box mb={3}>
          <TextField
            fullWidth
            label="Add New Note"
            multiline
            rows={3}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            disabled={loading}
          />
          <Box mt={1} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleAddNote}
              disabled={!newNote.trim() || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Add Note'}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" gutterBottom>
          Contact History
        </Typography>
        <List>
          {entry.contactNotes?.map((note, index) => (
            <React.Fragment key={index}>
              <ListItem>
                <ListItemText
                  primary={note.note}
                  secondary={formatDate(note.timestamp)}
                />
              </ListItem>
              {index < (entry.contactNotes?.length || 0) - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => onTransferToSlip(entry)}
          disabled={entry.status !== 'approved'}
        >
          Transfer to Slip
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactNotesDialog; 